import { Info, Lock } from "lucide-react";
import { cn } from "../lib/utils.js";
import { availableDays } from "../lib/leaveBalance.js";
import type { LeaveTypeConfig } from "../context/LeaveTypeConfigContext.js";
import { FloodCard, floodCardSlots, type FloodCardTone } from "./ui/flood-card.js";
import { Progress } from "./ui/progress.js";

// Permanently non-tracked for everyone, regardless of config-load timing -- a stable
// fact, kept hardcoded so there's no flash before useLeaveTypeConfig() resolves.
const ALWAYS_NO_BALANCE_TYPES = new Set(["WFH", "MATERNITY", "LOP"]);

const WFH_INCLUDED_DAYS = 5; // mirrors WFH_INCLUDED_DAYS in apps/api/src/modules/leave/leave.service.ts

const POLICY_INFO: Partial<Record<string, { status: string; unit: string }>> = {
  MATERNITY: { status: "Per-request only", unit: "per request" },
  WFH:       { status: "Per-quarter limit", unit: "per quarter" },
};

// Tint, bar/dot and pending colors all come from the card's tone.
const TYPE_TONES: Record<string, FloodCardTone> = {
  ANNUAL:    "info",
  CASUAL:    "success",
  COMP_OFF:  "warning",
  SICK:      "warning",
  WFH:       "accent",
  MATERNITY: "rose",
  PATERNITY: "cyan",
};

export interface LeaveBalanceRow {
  leaveType: string;
  allocated: number | string;
  used:      number | string;
  pending:   number | string;
  // Present for accrual-mode types (ANNUAL): how much has actually accrued so far this
  // year, which is less than `allocated` (the eventual full-year cap) for most of the
  // year. Absent/equal to `allocated` for every other leave type.
  accrued?:  number | string;
  // True when this leave type is probation-blocked (isProbationBlocked) AND the employee
  // is currently on probation, per the backend's real probation rule.
  probationBlocked?: boolean;
  // For an accrual type that's probation-blocked: what `accrued` would read the instant
  // probation clears (computed with onProbation:false) -- shown as a locked, accruing figure
  // instead of a bare 0. Absent otherwise.
  lockedAccrued?: number | string | undefined;
  // For a non-accrual type that's probation-blocked: the policy allocation it unlocks to.
  lockedAllocated?: number | string | undefined;
  // Probation end date (ISO) and whether it has passed awaiting HR's decision -- the lock reason.
  lockedUntil?: string | null | undefined;
  lockOverdue?: boolean | undefined;
}

export interface WfhQuarterUsage {
  includedUsed: number;
  extensionUsed: number;
  quarterLabel: string;
}

export type CardMode =
  | { kind: "numbered"; footnote?: string | undefined }
  // `total` is null for a type with no annual figure (e.g. per-request only).
  | { kind: "locked"; available: number; total: number | null; used: number; pending: number; unit: string; reason: string }
  | { kind: "restricted"; reason: string }
  | { kind: "info"; status: string; explain: string }
  | {
      kind: "wfh";
      includedAvailable: number;
      includedAllocated: number;
      extensionAvailable: number;
      extensionAllocated: number;
      quarterLabel: string;
    };

function capitalize(s: string) {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

// null/empty gender or maritalStatus means "not loaded yet" -- returns no restriction
// rather than a false positive, so an eligible employee never flashes a wrong
// "restricted" message while useMe()/the profile fetch is still in flight.
export function restrictionReason(
  cfg: Pick<LeaveTypeConfig, "genderRestriction" | "maritalStatusRestriction">,
  gender: string | null | undefined,
  maritalStatus: string | null | undefined,
): string | null {
  if (cfg.genderRestriction) {
    if (!gender) return null;
    if (gender.toLowerCase() !== cfg.genderRestriction.toLowerCase()) {
      return `Restricted to ${capitalize(cfg.genderRestriction)} employees.`;
    }
  }
  if (cfg.maritalStatusRestriction) {
    if (!maritalStatus) return null;
    if (maritalStatus.toLowerCase() !== cfg.maritalStatusRestriction.toLowerCase()) {
      return `Restricted to ${cfg.maritalStatusRestriction} employees.`;
    }
  }
  return null;
}

// Why a card is locked: on probation, until the end date -- or, once that has passed with no
// HR decision yet (or there is no date), until HR confirms.
export function lockReason(row: Pick<LeaveBalanceRow, "lockedUntil" | "lockOverdue">, accrual: boolean): string {
  const until = row.lockedUntil && !row.lockOverdue
    ? new Date(row.lockedUntil).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })
    : null;
  const base = until
    ? `Locked: you're on probation — unlocks after ${until}.`
    : "Locked: you're on probation — unlocks once HR confirms your probation.";
  return accrual ? `${base} Earned Leave keeps accruing meanwhile.` : base;
}

function lockedMode(row: LeaveBalanceRow, total: number | null, unit: string, accrual: boolean): CardMode {
  const used = Number(row.used);
  const pending = Number(row.pending);
  // For accrual types the available figure is what has accrued so far; `total` is the year's cap.
  const pool = accrual ? Number(row.lockedAccrued ?? 0) : (total ?? 0);
  return { kind: "locked", available: Math.max(0, pool - used - pending), total, used, pending, unit, reason: lockReason(row, accrual) };
}

export function resolveCardMode(
  leaveType: string,
  row: LeaveBalanceRow,
  cfg: LeaveTypeConfig | undefined,
  gender: string | null | undefined,
  maritalStatus: string | null | undefined,
  wfhUsage?: WfhQuarterUsage | null,
): CardMode {
  if (leaveType === "WFH") {
    const reason = cfg ? restrictionReason(cfg, gender, maritalStatus) : null;
    if (reason) return { kind: "restricted", reason };
    if (row.probationBlocked) return lockedMode({ ...row, used: wfhUsage?.includedUsed ?? 0, pending: 0 }, WFH_INCLUDED_DAYS, "this quarter", false);
    const extensionAllocated = Math.max(0, (cfg?.maxDaysPerRequest ?? WFH_INCLUDED_DAYS) - WFH_INCLUDED_DAYS);
    return {
      kind: "wfh",
      includedAllocated: WFH_INCLUDED_DAYS,
      includedAvailable: Math.max(0, WFH_INCLUDED_DAYS - (wfhUsage?.includedUsed ?? 0)),
      extensionAllocated,
      extensionAvailable: Math.max(0, extensionAllocated - (wfhUsage?.extensionUsed ?? 0)),
      quarterLabel: wfhUsage?.quarterLabel ?? "this quarter",
    };
  }

  const hasConsumption = Number(row.used) > 0 || Number(row.pending) > 0;
  const neverTracked = ALWAYS_NO_BALANCE_TYPES.has(leaveType) || cfg?.trackBalance === false;

  if (!neverTracked) {
    // A real tracked-type row exists -- trust that the backend only creates these for
    // eligible employees. Only override to "restricted" when gender/maritalStatus are
    // already loaded (not null) AND nothing has actually been consumed -- a legacy row
    // predating the HRMS-0562 eligibility fix. Anything consumed is always shown as real
    // numbers regardless, matching backfill-leave-balance-eligibility.ts's own decision:
    // anomalies are left untouched for manual review, never auto-hidden.
    // Gender/marital restriction (permanent) takes precedence over probation (temporary):
    // a permanently-ineligible card must never read "locked, will unlock".
    if (cfg && !hasConsumption) {
      const reason = restrictionReason(cfg, gender, maritalStatus);
      if (reason) return { kind: "restricted", reason };
    }
    // On probation every blocked card shows the figure it unlocks to, marked locked
    // (HRMS-0642) -- with or without consumption. The lock lifts as soon as the backend
    // stops flagging the row.
    if (row.probationBlocked) {
      const accrual = cfg?.accrualMode ?? row.lockedAccrued !== undefined;
      const total = accrual ? Number(row.allocated) : Number(row.lockedAllocated ?? row.allocated);
      return lockedMode(row, total, "", accrual);
    }
    // Earned Leave accrues month by month, so `available` (accrued-so-far) is less than
    // the full-year `allocated` cap for most of the year -- which reads like a bug without
    // a word of explanation. A footnote only for accrual-mode types says why.
    let footnote: string | undefined;
    if (cfg?.accrualMode) {
      const accrued = Number(row.accrued ?? row.allocated);
      const accruedStr = accrued % 1 === 0 ? String(accrued) : accrued.toFixed(2);
      footnote = `Accrues monthly — ${accruedStr} of ${Number(row.allocated)} days earned so far this year.`;
    }
    return { kind: "numbered", footnote };
  }

  const reason = cfg ? restrictionReason(cfg, gender, maritalStatus) : null;
  if (reason) return { kind: "restricted", reason };
  if (row.probationBlocked) return lockedMode(row, null, "", false);
  const info = POLICY_INFO[leaveType] ?? { status: "Not tracked", unit: "per request" };
  const max = cfg?.maxDaysPerRequest;
  return {
    kind: "info",
    status: info.status,
    explain: max
      ? `Up to ${max} days ${info.unit} — tracked separately, not as an annual balance.`
      : "Tracked separately, not as an annual balance.",
  };
}

export function LeaveInsightCards({
  balance,
  labels,
  typeConfig,
  gender,
  maritalStatus,
  wfhUsage,
}: {
  balance: LeaveBalanceRow[];
  labels: Record<string, string>;
  typeConfig?: Record<string, LeaveTypeConfig> | undefined;
  gender?: string | null | undefined;
  maritalStatus?: string | null | undefined;
  wfhUsage?: WfhQuarterUsage | null;
}) {
  // A leave type an admin has disabled is removed from every balance view, not shown as 0.
  const visible = balance.filter(b => typeConfig?.[b.leaveType]?.isEnabled !== false);
  if (!visible.length) return null;

  // Any probation-blocked row means the viewer is on probation -- surface the lock once,
  // above the grid, rather than relying on each card to say it (derived, no extra prop).
  const onProbation = visible.some(b => b.probationBlocked);

  return (
    <>
      {onProbation && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-[var(--color-warning-border)] bg-[var(--color-warning-bg-subtle)] px-4 py-3 text-[13px] text-[var(--color-warning-fg)]">
          <Lock size={15} className="mt-[1px] shrink-0" />
          <span>Leave is locked during your probation period and unlocks once it completes. Your Earned Leave still accrues in the background and is credited when probation ends.</span>
        </div>
      )}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,180px),1fr))] gap-3 mb-6">
        {visible.map((b) => {
        const allocated = Number(b.allocated);
        const used      = Number(b.used);
        const pending   = Number(b.pending);
        const available = availableDays(b);
        const mode = resolveCardMode(b.leaveType, b, typeConfig?.[b.leaveType], gender, maritalStatus, wfhUsage);

        const usedPct    = allocated > 0 ? Math.min(100, (used / allocated) * 100) : 0;
        const pendingPct = allocated > 0 ? Math.min(100 - usedPct, (pending / allocated) * 100) : 0;

        return (
          <FloodCard key={b.leaveType} tone={TYPE_TONES[b.leaveType] ?? "info"}>
            <div className={cn("mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-surface-500", floodCardSlots.muted)}>
              {labels[b.leaveType] ?? b.leaveType.replace(/_/g, " ")}
            </div>

            {mode.kind === "wfh" ? (
              <>
                <div className={cn("text-[26px] font-bold leading-none text-surface-900", floodCardSlots.strong)}>
                  {mode.includedAvailable}
                </div>
                <div className={cn("mb-2.5 mt-0.5 text-[11px] text-surface-500", floodCardSlots.muted)}>
                  of {mode.includedAllocated} days available {mode.quarterLabel}
                </div>

                <Progress
                  value={mode.includedAllocated > 0 ? ((mode.includedAllocated - mode.includedAvailable) / mode.includedAllocated) * 100 : 0}
                  barClassName={floodCardSlots.mark}
                  className={cn("h-1.5", floodCardSlots.track)}
                />

                <div className={cn("mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-surface-500", floodCardSlots.muted)}>
                  <Info size={12} className="mt-[1px] shrink-0" />
                  <span>{mode.extensionAvailable} of {mode.extensionAllocated} extra day(s) left via EL this quarter (0.5 EL/day).</span>
                </div>
              </>
            ) : mode.kind === "locked" ? (
              <>
                {mode.total !== null ? (
                  <>
                    <div className={cn("text-[26px] font-bold leading-none text-surface-900", floodCardSlots.strong)}>
                      {mode.available % 1 === 0 ? mode.available : mode.available.toFixed(2)}
                    </div>
                    <div className={cn("mb-2.5 mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-surface-500", floodCardSlots.muted)}>
                      of {mode.total} days{mode.unit ? ` ${mode.unit}` : ""}
                      <span className="inline-flex items-center gap-0.5 font-semibold"><Lock size={11} className="shrink-0" /> Locked</span>
                    </div>

                    <Progress
                      value={mode.total > 0 ? Math.min(100, (mode.used / mode.total) * 100) : 0}
                      secondaryValue={mode.total > 0 ? Math.min(100, (mode.pending / mode.total) * 100) : 0}
                      barClassName={floodCardSlots.mark}
                      secondaryClassName={floodCardSlots.markSoft}
                      className={cn("h-1.5", floodCardSlots.track)}
                    />

                    {(mode.used > 0 || mode.pending > 0) && (
                      <div className={cn("mt-2 flex gap-3 text-[11px] text-surface-500", floodCardSlots.muted)}>
                        <span>{mode.used % 1 === 0 ? mode.used : mode.used.toFixed(2)} used</span>
                        {mode.pending > 0 && <span>{mode.pending % 1 === 0 ? mode.pending : mode.pending.toFixed(2)} pending</span>}
                      </div>
                    )}
                  </>
                ) : (
                  <div className={cn("flex items-center gap-1.5 text-[13px] font-semibold text-surface-700", floodCardSlots.muted)}>
                    <Lock size={14} className="shrink-0" /> Locked
                  </div>
                )}

                <div className={cn("mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-surface-500", floodCardSlots.muted)}>
                  <Lock size={12} className="mt-[1px] shrink-0" />
                  <span>{mode.reason}</span>
                </div>
              </>
            ) : mode.kind !== "numbered" ? (
              <div className="flex flex-col gap-1">
                <div className={cn("flex items-center gap-1.5 text-[13px] font-semibold text-surface-700", floodCardSlots.muted)}>
                  {mode.kind === "restricted" ? <Lock size={14} className="shrink-0" /> : <Info size={14} className="shrink-0" />}
                  {mode.kind === "restricted" ? "Not applicable" : mode.status}
                </div>
                <div className={cn("text-[11px] leading-snug text-surface-500", floodCardSlots.muted)}>
                  {mode.kind === "restricted" ? mode.reason : mode.explain}
                </div>
              </div>
            ) : (
              <>
                <div className={cn("text-[26px] font-bold leading-none text-surface-900", floodCardSlots.strong)}>
                  {available % 1 === 0 ? available : available.toFixed(2)}
                </div>
                <div className={cn("mb-2.5 mt-0.5 text-[11px] text-surface-500", floodCardSlots.muted)}>
                  of {allocated} days available
                </div>

                <Progress value={usedPct} secondaryValue={pendingPct} barClassName={floodCardSlots.mark} secondaryClassName={floodCardSlots.markSoft} className={cn("h-1.5", floodCardSlots.track)} />

                <div className={cn("mt-2 flex gap-3 text-[11px] text-surface-500", floodCardSlots.muted)}>
                  <span className="flex items-center gap-1">
                    <span className={cn("inline-block h-2 w-2 rounded-sm", floodCardSlots.mark)} />
                    {used % 1 === 0 ? used : used.toFixed(2)} used
                  </span>
                  {pending > 0 && (
                    <span className="flex items-center gap-1">
                      <span className={cn("inline-block h-2 w-2 rounded-sm", floodCardSlots.markSoft)} />
                      {pending % 1 === 0 ? pending : pending.toFixed(2)} pending
                    </span>
                  )}
                </div>

                {mode.kind === "numbered" && mode.footnote && (
                  <div className={cn("mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-surface-500", floodCardSlots.muted)}>
                    <Info size={12} className="mt-[1px] shrink-0" />
                    <span>{mode.footnote}</span>
                  </div>
                )}
              </>
            )}
          </FloodCard>
        );
        })}
      </div>
    </>
  );
}
